import type { DocumentStatus } from '@/lib/document-status';
import { daysUntil } from '@/lib/time';
// Estado documental → etiqueta legible + clase de badge del sistema de diseño.
// Fuente única: cero enums crudos en la UI (UX-COPY-01).

export type { DocumentStatus };

export interface BadgeSpec {
  label: string;
  cls: 'b-ok' | 'b-warn' | 'b-red' | 'b-info' | 'b-mut';
}

const STATUS_MAP: Record<DocumentStatus, BadgeSpec> = {
  vigente: { label: 'Vigente', cls: 'b-ok' },
  en_revision: { label: 'En revisión', cls: 'b-warn' },
  en_aprobacion: { label: 'En aprobación', cls: 'b-warn' },
  borrador: { label: 'Borrador', cls: 'b-info' },
  reservado: { label: 'Reservado', cls: 'b-mut' },
  obsoleto: { label: 'Obsoleto', cls: 'b-mut' },
  anulado: { label: 'Anulado', cls: 'b-mut' },
};

export function statusBadge(status: DocumentStatus): BadgeSpec {
  return STATUS_MAP[status];
}

export type ReviewFlag = 'vencido' | 'por_vencer' | null;

/** Marca de revisión: vencido (< hoy), por vencer (≤ 90 días) o sin marca. */
export function reviewFlag(nextReviewAt: string | null, now: Date): ReviewFlag {
  if (!nextReviewAt) {
    return null;
  }
  // Días de negocio (Costa Rica), el mismo criterio del backend y del Panel.
  const d = daysUntil(nextReviewAt, now);
  if (d === null) {
    return null;
  }
  if (d < 0) {
    return 'vencido';
  }
  if (d <= 90) {
    return 'por_vencer';
  }
  return null;
}

const IN_TRAINING: BadgeSpec = { label: 'En capacitación', cls: 'b-info' };

/** Badge efectivo del documento: un vigente vencido se muestra como Vencido (rojo); un
 *  documento nuevo firmado con la capacitación abierta, En capacitación (G4). */
export function effectiveStatusBadge(
  status: DocumentStatus,
  nextReviewAt: string | null,
  now: Date,
  inTraining = false,
): BadgeSpec {
  if (status === 'vigente' && reviewFlag(nextReviewAt, now) === 'vencido') {
    return { label: 'Vencido', cls: 'b-red' };
  }
  if (inTraining && status === 'en_aprobacion') {
    return IN_TRAINING;
  }
  return statusBadge(status);
}

/** G4: chip adicional para una modificación en capacitación (el documento sigue vigente). */
export function trainingChip(status: DocumentStatus, inTraining: boolean): BadgeSpec | null {
  return inTraining && status === 'vigente' ? IN_TRAINING : null;
}
